import React, { useState, useEffect, useRef } from 'react';
import AddProjectModal from '../components/AddProjectModal';
import ConfirmDeleteModal from '../../../organisms/ConfirmDeleteModal';
import Toast from '../../../atoms/Toast';
import { API_ENDPOINTS, S3_BASE_URL } from '../../../../config';
import { api } from '../../../../utils/api';
import '../../../../styles/profile/projects-tab.css';

interface ProjectData {
  uid?: string;
  title: string;
  headline: string;
  description: string;
  releaseDate: string;
  achievements: string[];
  link: string;
  skills: string[];
  gallery: File[];
  existingImages?: string[];
}

interface Project {
  uid: string;
  project_name: string;
  headline: string;
  description: string | null;
  release_date: string | null;
  url: string | null;
  skills: string[] | null;
  achievements: string[] | null;
  gallery_urls: string[] | null;
  created_at: string;
  updated_at: string;
}

interface UserData {
  projects: Project[] | null;
}

interface ProjectsTabProps {
  userData: UserData | null;
  projects: Project[];
}

interface ProjectCardProps {
  project: Project;
  onEdit: (project: Project) => void;
  onDelete: (uid: string) => void;
}

function ProjectCard({ project, onEdit, onDelete }: ProjectCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const releaseDate = formatDate(project.release_date);

  const galleryImages = project.gallery_urls?.map(url => `${S3_BASE_URL}${url}`) || [];

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="project-card">
      <div className="project-card-header">
        <div className="project-card-placeholder">
          <span className="project-card-placeholder-icon">🚀</span>
        </div>
        <div className="project-card-info">
          <h3 className="project-card-title">{project.project_name}</h3>
          <p className="project-card-headline">{project.headline}</p>
          {project.url && (
            <a 
              href={project.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="project-card-url"
            >
              🔗 View Project
            </a>
          )}
        </div>
        {releaseDate && <p className="project-card-date">Released: {releaseDate}</p>}
        <div className="project-card-menu-container" ref={menuRef}>
          <button 
            className="project-card-menu" 
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            ⋮
          </button>
          {menuOpen && (
            <div className="project-card-dropdown">
              <button
                className="project-card-dropdown-item"
                onClick={() => {
                  onEdit(project);
                  setMenuOpen(false);
                }}
              >
                Edit project
              </button>
              <button
                className="project-card-dropdown-item project-card-dropdown-item--delete"
                onClick={() => {
                  onDelete(project.uid);
                  setMenuOpen(false);
                }}
              >
                Delete project
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="project-card-body">
        <div className="project-card-content">
          {project.description && (
            <p className="project-card-description">{project.description}</p>
          )}
          {project.skills && project.skills.length > 0 && (
            <div className="project-card-skills">
              <p className="project-card-skills-label">Skills:</p>
              <div className="project-card-skills-list">
                {project.skills.map((skill, idx) => (
                  <span key={idx} className="project-card-skill-chip">{skill}</span>
                ))}
              </div>
            </div>
          )}
          {project.achievements && project.achievements.length > 0 && (
            <div className="project-card-achievements">
              <p className="project-card-achievements-label">Achievements:</p>
              <ul className="project-card-achievements-list">
                {project.achievements.map((achievement, idx) => (
                  <li key={idx}>{achievement}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {galleryImages.length > 0 && (
          <div className="project-card-gallery">
            <div className="project-card-gallery-carousel">
              <img 
                src={galleryImages[currentImageIndex]} 
                alt={`${project.project_name} ${currentImageIndex + 1}`}
                className="project-card-gallery-image"
              />
              {galleryImages.length > 1 && (
                <>
                  <button 
                    className="project-card-gallery-nav project-card-gallery-nav--prev"
                    onClick={handlePrevImage}
                    type="button"
                  >
                    ‹
                  </button>
                  <button 
                    className="project-card-gallery-nav project-card-gallery-nav--next"
                    onClick={handleNextImage}
                    type="button"
                  >
                    ›
                  </button>
                  <div className="project-card-gallery-dots">
                    {galleryImages.map((_, idx) => (
                      <button
                        key={idx}
                        className={`project-card-gallery-dot ${idx === currentImageIndex ? 'project-card-gallery-dot--active' : ''}`}
                        onClick={() => setCurrentImageIndex(idx)}
                        type="button"
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProjectsTab({ userData, projects }: ProjectsTabProps) {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [userProjects, setUserProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [deleteUid, setDeleteUid] = useState<string | null>(null);

  useEffect(() => {
    if (projects && projects.length > 0) {
      setUserProjects(projects);
    }
  }, [projects]);

  const handleAdd = () => {
    setSelectedProject(null);
    setAddModalOpen(true);
  };

  const handleImport = () => {
  };

  const handleEdit = (project: Project) => {
    setSelectedProject(project);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (uid: string) => {
    setDeleteUid(uid);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteUid) return;

    setIsDeleting(true);

    try {
      
      await api.delete(API_ENDPOINTS.users.deleteProject(deleteUid));
      
      setUserProjects(userProjects.filter(proj => proj.uid !== deleteUid));
      setToast({ message: 'Project deleted successfully! ✓', type: 'success' });
      setDeleteModalOpen(false);
      setDeleteUid(null);
    } catch (error) {
      setToast({ message: 'Failed to delete project. Please try again.', type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddProject = async (data: ProjectData) => {
    setIsCreating(true);

    try {
      const formData = new FormData();

      const meta: any = {
        project_name: data.title.trim(),
        headline: data.headline.trim(),
      };

      if (data.uid) {
        meta.uid = data.uid;
      }

      if (data.description.trim()) meta.description = data.description.trim();
      if (data.releaseDate) meta.release_date = data.releaseDate;
      if (data.link.trim()) meta.url = data.link.trim();
      if (data.skills && data.skills.length > 0) meta.skills = data.skills;
      if (data.achievements && data.achievements.length > 0) {
        meta.achievements = data.achievements.filter(a => a.trim() !== '');
      }
      
      if (data.existingImages && data.existingImages.length > 0) {
        meta.gallery_urls = data.existingImages;
      }

      
      const metaString = JSON.stringify(meta);
      formData.append('meta', metaString);

      data.gallery.forEach((file, index) => {
        formData.append('images', file);
      });
      

      const response = await api.postFormData(API_ENDPOINTS.users.updateProject, formData);


      const isEditMode = !!data.uid;
      
      if (isEditMode) {
        setUserProjects(userProjects.map(proj => 
          proj.uid === data.uid ? { ...proj, ...response } : proj
        ));
        
        setTimeout(() => {
          setIsCreating(false);
          setEditModalOpen(false);
          setSelectedProject(null);
          setToast({ message: 'Project updated successfully! ✓', type: 'success' });
        }, 100);
      } else {
        if (response && response.uid) {
          setUserProjects([...userProjects, response]);
        }
        
        setTimeout(() => {
          setIsCreating(false);
          setAddModalOpen(false);
          setToast({ message: 'Project added successfully! ✓', type: 'success' });
        }, 100);
      }
    } catch (error) {
      setToast({ message: 'Failed to add project. Please try again.', type: 'error' });
      setIsCreating(false);
    }
  };

  const projectToModalData = (proj: Project | null): any => {
    if (!proj) return null;
    return {
      uid: proj.uid,
      title: proj.project_name,
      headline: proj.headline,
      description: proj.description,
      releaseDate: proj.release_date,
      link: proj.url,
      skills: proj.skills,
      achievements: proj.achievements,
      gallery: [],
      existingGalleryUrls: proj.gallery_urls,
    };
  };

  return (
    <>
      <div className="projects-tab">
        <div className="projects-tab-header">
          <h1 className="projects-tab-title">Projects</h1>
          <button className="projects-tab-btn projects-tab-btn--import" onClick={handleImport} type="button">
            <span className="projects-tab-btn-icon">⬇</span>
            Import
          </button>
        </div>

        <div className="projects-tab-content">
          <button 
            type="button" 
            className="projects-tab-add-btn"
            onClick={handleAdd}
          >
            <span className="projects-tab-add-icon">+</span>
          </button>

          {userProjects.length > 0 && (
            <div className="projects-list">
              {userProjects.map((proj) => (
                <ProjectCard 
                  key={proj.uid} 
                  project={proj}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <AddProjectModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={handleAddProject}
        isLoading={isCreating}
      />

      <AddProjectModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedProject(null);
        }}
        onAdd={handleAddProject}
        title="Edit Project"
        initialData={projectToModalData(selectedProject)}
        isEditMode={true}
        isLoading={isCreating}
      />

      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        itemName={userProjects.find(proj => proj.uid === deleteUid)?.project_name || 'this project'}
        itemType="Project"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeleteUid(null);
        }}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}
